<template>
  <Modal v-bind="modalConfig" @cancel="emit('cancel')" @confirm="onConfirm">
    <template #headline>{{ errorHeadline }}</template>
    <p>
      {{ errorMessage }}
    </p>
    <template #confirm>{{ t('lottery.errormodal.confirm') }}</template>
    <template #cancel>{{ t('lottery.errormodal.cancel') }}</template>
  </Modal>
</template>

<script lang="ts" setup>
import { ApolloError } from '@apollo/client/core'

import type { ModalProps } from '~/components/Modal/Modal.client.vue'

type CustomError = {
  headline: (() => string) | string
  message: (() => string) | string
  onConfirm?: () => void
}

type LotteryErrorModalEmits = {
  (e: 'cancel' | 'confirm'): void
}

type LotteryErrorModalProps = {
  error: Maybe<unknown>
}

const emit = defineEmits<LotteryErrorModalEmits>()
const props = defineProps<LotteryErrorModalProps>()

const { t } = useI18n()

const { navigateToLocalized } = useRouterUtils()

const lottery = computed(() => {
  const { params, query } = useRoute()
  return params.lotteryId || query?.p
})

const customErrors: Record<string, CustomError> = {
  'validation.plays.outofbounds': {
    headline: t('lottery.errormodal.validation.plays.outofbounds.title'),
    message: t('lottery.errormodal.validation.plays.outofbounds.message'),
  },
  'validation.playslip.duplicate': {
    headline: t('lottery.errormodal.validation.playslip.duplicate.title'),
    message: () =>
      t('lottery.errormodal.validation.playslip.duplicate.message', {
        lottery: t(`lottery.name.${lottery.value}`),
      }),
    onConfirm: () => {
      usePlaySlip().reset()
      navigateToLocalized({ path: `/${lottery.value}` })
    },
  },
}

const errorConfig = computed(() => {
  const key = !(props.error instanceof ApolloError)
    ? (props.error as Error)?.message
    : props.error.graphQLErrors[0]?.message

  if (key in customErrors) {
    return customErrors[key]
  }

  return {
    headline: t('lottery.errormodal.general.headline'),
    message: t('lottery.errormodal.general.message'),
  }
})

const errorHeadline = computed(() =>
  typeof errorConfig.value.headline === 'string'
    ? errorConfig.value.headline
    : errorConfig.value.headline(),
)

const errorMessage = computed(() =>
  typeof errorConfig.value.message === 'string'
    ? errorConfig.value.message
    : errorConfig.value.message(),
)

const modalConfig = computed<ModalProps>(() => {
  const hasConfirm = 'onConfirm' in errorConfig.value

  return {
    cancelVariant: hasConfirm ? 'primary' : 'primary-ghost',
    confirmVariant: hasConfirm ? 'primary-ghost' : 'primary',
    noCancel: !hasConfirm,
    tracking: {
      action: 'checkout',
      topicKey: 'playslip_validation_error',
    },
  }
})

const onConfirm = () => {
  if (errorConfig.value.onConfirm) {
    errorConfig.value.onConfirm()
    emit('confirm')
  }

  emit('cancel')
}
</script>
